import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/core';
import { Image, Pressable, Text, useWindowDimensions, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from 'nativewind';
import { useActionSheet } from '@expo/react-native-action-sheet';
import RenderHtml from 'react-native-render-html';
import { useLinkTo } from '@react-navigation/native';
import { Button, EventCardList, Icon, Layout, Loader, Tag } from '../../components/index';
import { isMobile, isMobileWeb, isNative, isTablet, isWeb } from '../../utils/responsive';
import { cln } from '../../utils/classnames';
import { Event, EventTranslationRole } from '../../client/interfaces';
import { ErrorType, TagVariant } from '../../resources/interfaces';
import {
  NavigationParamList,
  ScreenName,
  StackName,
  StackNavigationScreenProps,
} from '../../navigation/types';
import client from '../../client/client';
import { getTranslation } from '../../utils/translations';
import { useMessage } from '../../contexts/Messages/MessageContext';
import { useTags } from '../../contexts/Tag/TagContext';
import { addressPattern, handleHTMLLinkPress, handleLinkPress, openInMaps } from '../../utils';
import { getDateDetails } from '../../utils/getDateDetails';
import svgIcons from '../../assets';
import { EventsParamsContext } from '../../contexts/EventsParams/EventsParamsContext';
import analytics from '../../utils/analytics';

const EventScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.EventScreen>
> = ({ route, navigation }) => {
  const { t } = useTranslation();
  const [event, setEvent] = useState(null);
  const [similarEvents, setSimilarEvents] = useState<Event[]>(null);
  const { setMessage } = useMessage();
  const { tags } = useTags();
  const [tagIds, setTagIds] = useState<number[]>(null);
  const finalTagData = tags?.filter((tag) => tagIds?.includes(tag.id));
  const eventRef = useRef(null);
  const [hasCtaLabel, setHasCtaLabel] = useState<boolean>(false);
  const { colorScheme } = useColorScheme();
  const linkTo = useLinkTo();
  const { width } = useWindowDimensions();
  const MemoizedRenderHtml = React.memo(RenderHtml);

  const { setGetEventsParams } = useContext(EventsParamsContext);

  const { showActionSheetWithOptions } = useActionSheet();

  const renderedTags = finalTagData?.map((tag) => {
    return (
      <Tag
        key={tag.id}
        variant={TagVariant.Border}
        onPress={() => {
          setGetEventsParams((prevState) => {
            return { ...prevState, tags: [`${tag.id}`] };
          });
          navigation.navigate(ScreenName.EventsScreen);
        }}
        margin="mr-3 mb-3"
      >
        {tag.translationValue}
      </Tag>
    );
  });

  const renderers = useMemo(
    () => ({
      a: ({ tnode }) => {
        const href = tnode.attributes.href;

        return (
          <Text
            onPress={() => handleHTMLLinkPress(href, navigation, linkTo)}
            style={{
              color: colorScheme === 'dark' ? '#EDF115' : '#623BFF',
              textDecorationLine: 'none',
            }}
          >
            {tnode.data}
          </Text>
        );
      },
    }),
    [colorScheme],
  );

  // FETCHING DATA
  const fetchEvent = async () => {
    try {
      if (eventRef.current && isNative) {
        eventRef?.current?.scrollTo({ y: 10, animated: false });
      } else if (eventRef.current && !isNative) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
      const eventRes = await client.getEvent(route.params.postId);
      setEvent(eventRes);
      setHasCtaLabel(!!getTranslation(eventRes.translations, EventTranslationRole.ctaLabel));

      const eventTitle = getTranslation(eventRes.translations, EventTranslationRole.title);

      navigation.setOptions({
        title: eventTitle,
      });

      try {
        await analytics().logScreenView({
          screenName: eventTitle,
          screenClass: ScreenName.EventScreen,
        });
      } catch (e) {
        console.error(e);
      }

      const tagIds = eventRes?.tags?.map((tag) => {
        return tag.id;
      });
      setTagIds(tagIds);

      const similarEventsRes = await client.getSimilarEvents({
        postId: route.params.postId,
        tagIds: tagIds,
        limit: 3,
      });

      setSimilarEvents(similarEventsRes);
    } catch (error) {
      const notFoundErrors = [404, 422];
      setMessage({ message: error.message, type: 'error' });
      navigation.navigate(StackName.ErrorStack, {
        type: notFoundErrors.includes(error.response.status)
          ? ErrorType.not_found
          : ErrorType.internal_server_error,
      });
      navigation.goBack();
    }
  };

  useFocusEffect(
    useCallback(() => {
      route?.params?.postId && fetchEvent();
    }, [route?.params?.postId]),
  );

  const toggleBookmark = (event: Event) => {
    setSimilarEvents((prev) =>
      prev.map((a) => (a.id === event.id ? { ...a, isBookmarked: !a?.isBookmarked } : a)),
    );
  };

  const dateDetails = getDateDetails(event?.relatedItemDetail);

  const fallbackImage =
    colorScheme === 'light'
      ? require('../../assets/fallback.jpg')
      : require('../../assets/fallback-dark.jpg');

  // TODO: Replace with google places validation when available
  const isAddress = addressPattern.test(event?.relatedItemDetail.location);

  // eslint-disable-next-line eqeqeq
  return event && event?.id == route?.params?.postId ? (
    <Layout
      title={getTranslation(event.translations, EventTranslationRole.title)}
      isScrollView={isMobile}
      isArticleStyle={true}
      refProp={eventRef}
      titleMarginBottom={false}
    >
      <View testID="event-screen" className="max-w-[640px] w-full self-center mt-2">
        <View className={cln(isMobile && 'px-0', 'w-full mt-0')}>
          <View
            className={cln(
              isMobile ? 'mb-0' : 'mt-2 mb-6',
              'flex-row justify-between items-center',
            )}
            style={{
              minHeight: 40,
              marginBottom: 24,
              marginTop: 0,
            }}
          >
            <View className="w-full">
              <View className="mb-6">
                <Text className="font-[sans-400] text-xs text-neutral-950 dark:text-neutral-50">
                  {`${t('article:uploaded_by')}: ${!event.publisher ? '' : event.publisher}`}
                </Text>
              </View>

              <View className="w-full">
                <View className="flex-row items-center relative w-full mb-1">
                  <Icon
                    icon={svgIcons.calendarIcon}
                    mobileSize={{ width: 24, height: 24 }}
                    webSize={{ width: 24, height: 24 }}
                    classNames={'mr-3 mb-[2px]'}
                  />
                  <View className="flex-row items-start flex-1 flex-wrap">
                    <Text className="text-neutral-930 dark:text-neutral-50 font-[sans-400] text-calendarCardTitle md:text-body mr-2">
                      {dateDetails.dateFirstLine} {dateDetails.dateSecondLine ? '-' : ''}
                    </Text>
                    <Text className="text-neutral-930 dark:text-neutral-50 font-[sans-400] text-calendarCardTitle md:text-body mr-2">
                      {dateDetails.dateSecondLine}
                    </Text>
                  </View>
                </View>
                {isAddress ? (
                  <Pressable
                    className={cln('flex-row items-center mb-1', isMobile && 'min-h-[40px]')}
                    onPress={() =>
                      openInMaps(
                        event?.relatedItemDetail.location,
                        showActionSheetWithOptions,
                        setMessage,
                      )
                    }
                  >
                    <Icon
                      icon={svgIcons.pinIconPrimary}
                      mobileSize={{ width: 24, height: 24 }}
                      webSize={{ width: 24, height: 24 }}
                      classNames={'mr-3'}
                    />
                    <View className="flex-row items-start flex-1 flex-wrap">
                      <Text className="text-primary-600 dark:text-primary-500 font-[sans-400] text-calendarCardTitle md:text-body mr-2 underline">
                        {event?.relatedItemDetail?.location}
                      </Text>
                    </View>
                  </Pressable>
                ) : (
                  <View className="flex-row items-center mb-1">
                    <Icon
                      icon={svgIcons.pinIcon}
                      mobileSize={{ width: 24, height: 24 }}
                      webSize={{ width: 24, height: 24 }}
                      classNames={'mr-3'}
                    />
                    <View className="flex-row items-start flex-1 flex-wrap">
                      <Text className="text-neutral-930 dark:text-neutral-50 font-[sans-400] text-calendarCardTitle md:text-body mr-2">
                        {event?.relatedItemDetail?.location}
                        {event?.relatedItemDetail?.joinLink
                          ? `${event?.relatedItemDetail?.location ? ' / ' : ''}${t('common:online')}`
                          : ``}
                      </Text>
                    </View>
                  </View>
                )}
                {event?.relatedItemDetail.isEnglish ? (
                  <View className="flex-row items-center mb-1">
                    <Icon
                      icon={svgIcons.speechBubbleIcon}
                      mobileSize={{ width: 23, height: 23 }}
                      webSize={{ width: 23, height: 23 }}
                      classNames={'mr-3'}
                    />
                    <View className="flex-row items-start flex-1 flex-wrap">
                      <Text className="text-neutral-930 dark:text-neutral-50 font-[sans-400] text-calendarCardTitle md:text-body mr-2">
                        {t('admin:event_edit:is_english')}
                      </Text>
                    </View>
                  </View>
                ) : (
                  <></>
                )}
                {event?.relatedItemDetail.joinLink ? (
                  <Pressable
                    className="flex-row items-center justify-start"
                    onPress={() => handleLinkPress(event.relatedItemDetail.joinLink)}
                  >
                    <Icon
                      icon={svgIcons.filmIconPrimary}
                      mobileSize={{ width: 24, height: 24 }}
                      webSize={{ width: 24, height: 24 }}
                      classNames={'mr-3'}
                    />
                    <Text className="text-primary-600 dark:text-primary-500 text-calendarCardTitle md:text-body font-[sans-400] underline">
                      {t('today:join_online')}
                    </Text>
                  </Pressable>
                ) : (
                  <></>
                )}
              </View>
            </View>
          </View>
          <View />
        </View>
        <Tag
          onPress={async () => {
            if (event?.isBookmarked) {
              await client.deletePostBookmark(event?.id);
              setEvent((prevValue) => {
                return { ...prevValue, isBookmarked: false };
              });
            } else {
              await client.createPostBookmark(event?.id);
              setEvent((prevValue) => {
                return { ...prevValue, isBookmarked: true };
              });
            }
          }}
          variant={TagVariant.IconTag}
          iconName={event?.isBookmarked ? 'bookmarkFilledIcon' : 'bookmarkBorder'}
          isFilled={event?.isBookmarked}
          margin="mr-3"
          width={isMobile && !isTablet && 'w-full'}
        >
          {event.isBookmarked ? t('event:event_saved') : t('event:save_event')}
        </Tag>

        <View
          className={isMobile ? 'my-6' : 'my-6'}
          style={{ aspectRatio: 4 / 3, width: '100%', overflow: 'hidden' }}
        >
          <Image
            testID="event-image"
            source={event.imageUrl ? { uri: event.imageUrl } : fallbackImage}
            resizeMode={'cover'}
            style={{ height: '100%', maxWidth: '100%' }}
          />
        </View>
        <View testID={'rendered-event-tags'} className="flex-row flex-wrap mb-6">
          {renderedTags}
        </View>

        <View
          className={cln(
            isMobile && 'mt-0',
            'self-center mb-6 sm:mb-10 w-full [&_*]:text-neutral-50',
          )}
        >
          <MemoizedRenderHtml
            contentWidth={width}
            source={{
              html: getTranslation(event.translations, EventTranslationRole.description),
            }}
            baseStyle={{
              color: colorScheme === 'dark' ? '#f6f6f6' : '#101010',
              fontSize: 16,
              fontFamily: 'sans-400',
              lineHeight: 24,
            }}
            renderers={renderers}
          />
        </View>

        {hasCtaLabel && (
          <View
            style={{
              marginBottom:
                // On phone without similar articles
                isNative && similarEvents?.length < 1
                  ? 40
                  : isNative && similarEvents?.length >= 1
                    ? 0
                    : // On web phone width with similar articles
                      isMobileWeb && similarEvents?.length > 0
                      ? 0
                      : // On web phone width without similar articles
                        isMobileWeb && similarEvents?.length < 1
                        ? 40
                        : // On web bigger screens without similar articles
                          isWeb && similarEvents?.length <= 0
                          ? 0
                          : // On web bigger screens with similar articles
                            40,
            }}
          >
            <Button
              text={getTranslation(event.translations, EventTranslationRole.ctaLabel)}
              variant={'fill'}
              onPress={() =>
                handleLinkPress(getTranslation(event.translations, EventTranslationRole.ctaLink))
              }
              size={'medium'}
              style={{ alignItems: 'center' }}
            />
          </View>
        )}
      </View>
      {similarEvents?.length >= 1 && (
        <View
          className={cln(isNative ? 'mb-0 left-[-32px] w-full' : 'mb-0')}
          style={{
            marginTop: isNative && !hasCtaLabel ? 64 : isMobileWeb && !hasCtaLabel ? 16 : 90,
          }}
        >
          <EventCardList
            items={similarEvents}
            toggleBookmark={toggleBookmark}
            title={t('event:similar_events')}
            testID={'similar-events'}
            leftArrowTestId={'similar-events-left-arrow'}
            rightArrowTestId={'similar-events-right-arrow'}
          />
        </View>
      )}
    </Layout>
  ) : (
    <View className=" bg-neutral-50 dark:bg-neutral-950 h-full w-full items-center justify-center">
      <Loader />
    </View>
  );
};

export default EventScreen;
